import type { IntegerAmount } from '@actual-app/core/shared/util';

import type { Categorias, FilaGasto, Movimiento } from './calculos';
import {
  ESCALA_MAD,
  esPuntualEnSerie,
  mad,
  mediana,
  MINIMO_MESES_DISPERSION,
  recortarInicioSinDatos,
  referenciaHabitual,
} from './estadisticaRobusta';
import type { Periodo } from './periodo';

/**
 * Cálculos puros de la pantalla «Gastos» (rosa de los vientos): anillo con
 * las 6 categorías que más pesan + «Resto», comparación con el periodo
 * anterior a igual día, ritmo frente a lo habitual, gastos puntuales y fijos
 * frente a variables. Reglas generales, nunca por nombre de categoría. Todo
 * en céntimos enteros (gasto en positivo).
 */

/** Clave de una categoría en esta pantalla (`sin` = sin categoría). */
export type Clave = string;
export const CLAVE_SIN = 'sin';
export const CLAVE_RESTO = 'resto';

/** Porciones con nombre en el anillo; el resto va junto en «Resto». */
export const PORCIONES = 6;
/** Por debajo de esta fracción, el % de la porción va fuera, con línea guía. */
export const MINIMO_ETIQUETA_DENTRO = 0.07;
/** Ritmo a partir del cual una categoría «se te va más rápido» (+15 %). */
export const RITMO_RAPIDO = 1.15;
/** Ritmo por debajo del cual va «más despacio» (−25 %). */
export const RITMO_LENTO = 0.75;
/** Exceso mínimo (o defecto) en € para destacar un ritmo: 10 €. */
export const MINIMO_EXCESO = 1000;
/** Cambio mínimo frente al periodo anterior para enseñar ▲▼: 10 % y 10 €. */
export const MINIMO_CAMBIO_PCT = 0.1;
export const MINIMO_CAMBIO_IMPORTE = 1000;
/** Meses de historial para «lo habitual». */
export const MESES_HABITUAL = 12;
/** Fijo por importe estable: dispersión (MAD) < 2 % de la mediana. */
export const DISPERSION_FIJO = 0.02;

export function claveDe(categoria: string | null): Clave {
  return categoria ?? CLAVE_SIN;
}

/** Gasto (positivo) por categoría y mes. */
export type GastoPorMes = ReadonlyMap<
  Clave,
  ReadonlyMap<string, IntegerAmount>
>;

/**
 * Gasto por categoría y mes a partir de los movimientos (como
 * `gastoPorCategoria`): sin categorías de ingreso; lo que no tiene categoría
 * solo suma sus salidas; un neto positivo (devoluciones) cuenta como 0.
 */
export function gastoPorCategoriaYMes(
  movimientos: readonly Pick<Movimiento, 'mes' | 'categoria' | 'importe'>[],
  categorias: Categorias,
): Map<Clave, Map<string, IntegerAmount>> {
  const neto = new Map<Clave, Map<string, number>>();
  for (const m of movimientos) {
    if (m.categoria != null && categorias.get(m.categoria)?.esIngreso) {
      continue;
    }
    if (m.categoria == null && m.importe > 0) {
      continue;
    }
    const clave = claveDe(m.categoria);
    let porMes = neto.get(clave);
    if (!porMes) {
      porMes = new Map();
      neto.set(clave, porMes);
    }
    porMes.set(m.mes, (porMes.get(m.mes) ?? 0) + m.importe);
  }
  for (const porMes of neto.values()) {
    for (const [mes, v] of porMes) {
      porMes.set(mes, v < 0 ? -v : 0);
    }
  }
  return neto;
}

// ---------------------------------------------------------------------------
// Periodo comparable
// ---------------------------------------------------------------------------

/** Meses que se retrocede para el periodo comparable; `null` sin él («Todo»). */
export function desfaseComparacion(periodo: Periodo): number | null {
  switch (periodo.tipo) {
    case 'mes':
      return 1;
    case 'ultimos':
      return periodo.meses;
    case 'ano':
    case 'ano-pasado':
      return 12;
    default:
      return null;
  }
}

function restarMeses(mes: string, n: number): string {
  const indice = Number(mes.slice(0, 4)) * 12 + Number(mes.slice(5, 7)) - 1 - n;
  const anio = Math.floor(indice / 12);
  return `${anio}-${String((indice % 12) + 1).padStart(2, '0')}`;
}

/** Meses del periodo comparable (mismo largo, retrocediendo `desfase`). */
export function mesesComparacion(
  periodo: Periodo,
  meses: readonly string[],
): string[] | null {
  const desfase = desfaseComparacion(periodo);
  if (desfase == null || meses.length === 0) {
    return null;
  }
  return meses.map(m => restarMeses(m, desfase));
}

/** Hasta 12 meses anteriores al periodo (base de «lo habitual»). */
export function mesesPrevios(
  meses: readonly string[],
  n = MESES_HABITUAL,
): string[] {
  if (meses.length === 0) {
    return [];
  }
  return Array.from({ length: n }, (_, i) => restarMeses(meses[0], n - i));
}

/**
 * Rangos de fechas «del día 1 al día `dia`» de cada mes (el día se recorta
 * al último del mes), para pedir en UNA consulta lo gastado hasta ese día.
 */
export function rangosHastaDia(
  meses: readonly string[],
  dia: number,
): Array<{ desde: string; hasta: string }> {
  return meses.map(mes => {
    const ultimo = new Date(
      Number(mes.slice(0, 4)),
      Number(mes.slice(5, 7)),
      0,
    ).getDate();
    const d = String(Math.min(dia, ultimo)).padStart(2, '0');
    return { desde: `${mes}-01`, hasta: `${mes}-${d}` };
  });
}

// ---------------------------------------------------------------------------
// Análisis
// ---------------------------------------------------------------------------

export type PuntualDetectado = {
  clave: Clave;
  mes: string;
  /** Gasto de la categoría ese mes. */
  importe: IntegerAmount;
  /** Lo habitual de la categoría (mediana de los meses previos). */
  habitual: IntegerAmount;
  /** Veces lo habitual. */
  veces: number;
  /** Lo que se deja fuera del ritmo: lo que pasa de lo habitual. */
  exceso: IntegerAmount;
};

export type FilaAnalisis = {
  clave: Clave;
  categoria: string | null;
  importe: IntegerAmount;
  /** Fracción del total del periodo. */
  fraccion: number;
  /** Periodo comparable (a igual día si el periodo está en curso). */
  anterior: IntegerAmount | null;
  /** Parte puntual (fuera del ritmo). */
  puntual: IntegerAmount;
  /** Lo habitual para este periodo (hasta hoy si está en curso). */
  esperado: IntegerAmount | null;
  /** (importe − puntual) / esperado. */
  ritmo: number | null;
  /** (importe − puntual) − esperado. */
  exceso: IntegerAmount | null;
  esFijo: boolean;
};

export type EntradaAnalisis = {
  /** Gasto del periodo por categoría (`gastoPorCategoria`). */
  filas: readonly FilaGasto[];
  /** Meses del periodo, en orden. */
  meses: readonly string[];
  /** Meses del periodo comparable (`mesesComparacion`) o null. */
  comparacion: readonly string[] | null;
  /** Meses previos (`mesesPrevios`). */
  previos: readonly string[];
  /** Gasto por categoría y mes completo (cubre previos, comparación y periodo). */
  porMes: GastoPorMes;
  /**
   * Gasto por categoría y mes hasta el día de hoy, de los meses previos, si
   * el periodo acaba en el mes en curso; `null` si el periodo está cerrado.
   */
  hastaDia: GastoPorMes | null;
  /** Categorías marcadas como «Pago fijo». */
  fijosMarcados: ReadonlySet<string>;
};

export type AnalisisGastos = {
  total: IntegerAmount;
  totalAnterior: IntegerAmount | null;
  filas: FilaAnalisis[];
  puntuales: PuntualDetectado[];
  /** Hay al menos 3 meses con gasto antes del periodo. */
  historialSuficiente: boolean;
  /** `marcados`: los «Pago fijo» de la app; `estables`: importe estable. */
  criterioFijos: 'marcados' | 'estables';
};

function valor(porMes: GastoPorMes, clave: Clave, mes: string): number {
  return porMes.get(clave)?.get(mes) ?? 0;
}

/** Fijo por importe estable: casi igual cada mes en los meses previos. */
export function esImporteEstable(valores: readonly number[]): boolean {
  const serie = recortarInicioSinDatos(valores);
  if (serie.length < MINIMO_MESES_DISPERSION) {
    return false;
  }
  const med = mediana(serie);
  if (med <= 0) {
    return false;
  }
  return mad(serie, med) / ESCALA_MAD / med < DISPERSION_FIJO;
}

/**
 * Meses puntuales de una serie (índices): la regla de `esPuntualEnSerie`
 * (más de 5 veces lo habitual, por encima del umbral robusto y sin
 * repetirse) con lo habitual de los meses `base` (índices) con gasto.
 */
function indicesPuntuales(
  serie: ReadonlyArray<{ mes: string; importe: number }>,
  base: readonly number[],
  candidatos: readonly number[],
): { puntuales: Set<number>; habitual: number } {
  const conGasto = base.map(i => serie[i].importe).filter(v => v > 0);
  if (conGasto.length < MINIMO_MESES_DISPERSION) {
    return { puntuales: new Set(), habitual: 0 };
  }
  const ref = referenciaHabitual(conGasto);
  const puntuales = new Set(
    candidatos.filter(i => esPuntualEnSerie(i, serie, ref.mediana, ref.umbral)),
  );
  return { puntuales, habitual: ref.mediana };
}

export function analizarGastos(e: EntradaAnalisis): AnalisisGastos {
  const total = e.filas.reduce((s, f) => s + f.importe, 0);
  const enCurso = e.hastaDia != null;
  const n = e.meses.length;
  const mesesConGasto = e.previos.filter(mes =>
    [...e.porMes.values()].some(p => (p.get(mes) ?? 0) > 0),
  ).length;
  const historialSuficiente = mesesConGasto >= MINIMO_MESES_DISPERSION;
  const criterioFijos = e.fijosMarcados.size > 0 ? 'marcados' : 'estables';
  const ultimoComparable = e.comparacion?.[e.comparacion.length - 1];

  const puntuales: PuntualDetectado[] = [];
  const filas = e.filas.map<FilaAnalisis>(f => {
    const clave = claveDe(f.categoria);
    const previos = e.previos.map(mes => valor(e.porMes, clave, mes));

    // Periodo comparable: a igual día en el último mes si está en curso.
    let anterior: number | null = null;
    if (e.comparacion) {
      anterior = e.comparacion.reduce(
        (s, mes) =>
          s +
          (enCurso && mes === ultimoComparable
            ? valor(e.hastaDia!, clave, mes)
            : valor(e.porMes, clave, mes)),
        0,
      );
    }

    // Gastos puntuales (en el periodo y en los meses previos).
    const serie = [...e.previos, ...e.meses].map(mes => ({
      mes,
      importe: valor(e.porMes, clave, mes),
    }));
    const iPrevios = e.previos.map((_, i) => i);
    const iPeriodo = e.meses.map((_, i) => e.previos.length + i);
    const { puntuales: marcados, habitual } = indicesPuntuales(
      serie,
      iPrevios,
      [...iPrevios, ...iPeriodo],
    );
    let puntual = 0;
    for (const i of iPeriodo) {
      if (marcados.has(i)) {
        const exceso = serie[i].importe - habitual;
        puntual += exceso;
        puntuales.push({
          clave,
          mes: serie[i].mes,
          importe: serie[i].importe,
          habitual,
          veces: serie[i].importe / habitual,
          exceso,
        });
      }
    }

    // Lo habitual: mediana de los meses previos con datos, sin puntuales.
    let esperado: number | null = null;
    const desde = previos.findIndex(v => v !== 0);
    const validos =
      desde < 0 ? [] : iPrevios.filter(i => i >= desde && !marcados.has(i));
    if (historialSuficiente && validos.length >= MINIMO_MESES_DISPERSION) {
      const hab = mediana(validos.map(i => previos[i]));
      if (enCurso) {
        const habDia = mediana(
          validos.map(i => valor(e.hastaDia!, clave, e.previos[i])),
        );
        esperado = hab * (n - 1) + habDia;
      } else {
        esperado = hab * n;
      }
    }
    const sinPuntual = f.importe - puntual;
    const ritmo =
      esperado != null && esperado > 0 ? sinPuntual / esperado : null;

    return {
      clave,
      categoria: f.categoria,
      importe: f.importe,
      fraccion: total > 0 ? f.importe / total : 0,
      anterior,
      puntual,
      esperado,
      ritmo,
      exceso: esperado != null ? sinPuntual - esperado : null,
      esFijo:
        f.categoria != null &&
        (criterioFijos === 'marcados'
          ? e.fijosMarcados.has(f.categoria)
          : esImporteEstable(previos)),
    };
  });

  const totalAnterior = e.comparacion
    ? sumarAnterior(e, enCurso, ultimoComparable)
    : null;
  puntuales.sort((a, b) => b.exceso - a.exceso);
  return {
    total,
    totalAnterior,
    filas,
    puntuales,
    historialSuficiente,
    criterioFijos,
  };
}

/** Total del periodo comparable, de todas las categorías (también las que hoy no tienen gasto). */
function sumarAnterior(
  e: EntradaAnalisis,
  enCurso: boolean,
  ultimo: string | undefined,
): number {
  let suma = 0;
  for (const mes of e.comparacion ?? []) {
    const fuente = enCurso && mes === ultimo ? e.hastaDia! : e.porMes;
    for (const porMes of fuente.values()) {
      suma += porMes.get(mes) ?? 0;
    }
  }
  return suma;
}

// ---------------------------------------------------------------------------
// Anillo, frase, ritmo, fijos y cambios
// ---------------------------------------------------------------------------

export type Porcion = {
  clave: Clave;
  importe: IntegerAmount;
  fraccion: number;
  /** Categorías que agrupa («Resto»). */
  cuantas: number;
};

/** Las 6 categorías que más pesan y, si hay más, «Resto». */
export function agruparPorciones(
  filas: ReadonlyArray<{ clave: Clave; importe: IntegerAmount }>,
  maximo = PORCIONES,
): Porcion[] {
  const total = filas.reduce((s, f) => s + f.importe, 0);
  const orden = [...filas].sort((a, b) => b.importe - a.importe);
  const fr = (v: number) => (total > 0 ? v / total : 0);
  const porciones = orden.slice(0, maximo).map(f => ({
    clave: f.clave,
    importe: f.importe,
    fraccion: fr(f.importe),
    cuantas: 1,
  }));
  const resto = orden.slice(maximo);
  if (resto.length > 0) {
    const importe = resto.reduce((s, f) => s + f.importe, 0);
    porciones.push({
      clave: CLAVE_RESTO,
      importe,
      fraccion: fr(importe),
      cuantas: resto.length,
    });
  }
  return porciones;
}

/**
 * Dónde va la etiqueta del % de una porción: ángulo del centro de la
 * porción (radianes, 0 = norte, en el sentido del reloj) y si cabe dentro.
 */
export function etiquetaPorcion(
  porciones: readonly Porcion[],
  clave: Clave,
): { angulo: number; dentro: boolean; fraccion: number } | null {
  let acumulado = 0;
  for (const p of porciones) {
    if (p.clave === clave) {
      return {
        angulo: (acumulado + p.fraccion / 2) * 2 * Math.PI,
        dentro: p.fraccion >= MINIMO_ETIQUETA_DENTRO,
        fraccion: p.fraccion,
      };
    }
    acumulado += p.fraccion;
  }
  return null;
}

/** Cuántas categorías (de mayor a menor) se llevan la mitad del gasto. */
export function categoriasMitad(
  filas: ReadonlyArray<{ importe: IntegerAmount }>,
): number {
  const total = filas.reduce((s, f) => s + f.importe, 0);
  if (total <= 0) {
    return 0;
  }
  const orden = [...filas].sort((a, b) => b.importe - a.importe);
  let acumulado = 0;
  let k = 0;
  for (const f of orden) {
    acumulado += f.importe;
    k++;
    if (acumulado * 2 >= total) {
      break;
    }
  }
  return k;
}

/** Cambio frente al periodo anterior; `null` si no llega al 10 % y 10 €. */
export function cambioVisible(
  actual: IntegerAmount,
  anterior: IntegerAmount | null,
): number | null {
  if (anterior == null || anterior <= 0) {
    return null;
  }
  const v = actual / anterior - 1;
  return Math.abs(v) >= MINIMO_CAMBIO_PCT &&
    Math.abs(actual - anterior) >= MINIMO_CAMBIO_IMPORTE
    ? v
    : null;
}

/** Variación del total frente al periodo comparable (para la píldora). */
export function variacionTotal(
  total: IntegerAmount,
  anterior: IntegerAmount | null,
): { fraccion: number; tono: 'neutro' | 'mal' | 'aviso' | 'bien' } | null {
  if (anterior == null || anterior <= 0) {
    return null;
  }
  const fraccion = total / anterior - 1;
  const tono =
    Math.abs(fraccion) < 0.03
      ? 'neutro'
      : fraccion > 0
        ? fraccion > 0.1
          ? 'mal'
          : 'aviso'
        : 'bien';
  return { fraccion, tono };
}

/** Hasta 4 que van más rápido (≥ +15 % y ≥ 10 €) y hasta 2 más despacio. */
export function ritmos(filas: readonly FilaAnalisis[]): {
  rapidas: FilaAnalisis[];
  lentas: FilaAnalisis[];
} {
  const rapidas = filas
    .filter(
      f =>
        f.ritmo != null &&
        f.exceso != null &&
        f.ritmo >= RITMO_RAPIDO &&
        f.exceso >= MINIMO_EXCESO,
    )
    .sort((a, b) => (b.exceso ?? 0) - (a.exceso ?? 0))
    .slice(0, 4);
  const lentas = filas
    .filter(
      f =>
        f.ritmo != null &&
        f.exceso != null &&
        f.ritmo <= RITMO_LENTO &&
        -f.exceso >= MINIMO_EXCESO,
    )
    .sort((a, b) => (a.exceso ?? 0) - (b.exceso ?? 0))
    .slice(0, 2);
  return { rapidas, lentas };
}

/** Ancho del medidor de ritmo (0 → lo habitual en el 50 % → el doble). */
export function anchoMedidor(ritmo: number): number {
  return (Math.min(Math.max(ritmo, 0), 2) / 2) * 100;
}

export function fijosYVariables(filas: readonly FilaAnalisis[]): {
  fijos: IntegerAmount;
  variables: IntegerAmount;
  categoriasFijas: number;
} {
  let fijos = 0;
  let variables = 0;
  let categoriasFijas = 0;
  for (const f of filas) {
    if (f.esFijo) {
      fijos += f.importe;
      categoriasFijas++;
    } else {
      variables += f.importe;
    }
  }
  return { fijos, variables, categoriasFijas };
}

/** Marca de la lista: «nuevo» (antes < 1 € y ahora ≥ 1 €), ▲▼ o nada. */
export type MarcaCambio =
  | { tipo: 'nuevo' }
  | { tipo: 'cambio'; fraccion: number }
  | null;

/** Importe mínimo (1 €) para decir que una categoría «tenía» gasto. */
export const MINIMO_NUEVO = 100;

/**
 * «nuevo» si en el periodo comparable no hubo gasto (< 1 €) y en este sí;
 * si no, el ▲▼ de `cambioVisible` (sin la parte puntual). Sin periodo
 * comparable («Todo»), nada.
 */
export function marcaCambio(
  fila: Pick<FilaAnalisis, 'importe' | 'puntual' | 'anterior'>,
): MarcaCambio {
  if (fila.anterior == null) {
    return null;
  }
  if (fila.anterior < MINIMO_NUEVO) {
    return fila.importe >= MINIMO_NUEVO ? { tipo: 'nuevo' } : null;
  }
  const v = cambioVisible(fila.importe - fila.puntual, fila.anterior);
  return v == null ? null : { tipo: 'cambio', fraccion: v };
}

// ---------------------------------------------------------------------------
// Movimiento de cada gasto puntual
// ---------------------------------------------------------------------------

/** Fila de la consulta de movimientos de los meses puntuales. */
export type FilaMovimiento = {
  id: string;
  parent_id: string | null;
  date: string; // 'yyyy-MM-dd'
  category: string | null;
  payee: string | null;
  notes: string | null;
  amount: IntegerAmount;
};

export type MovimientoPuntual = {
  /** Movimiento que se abre (el padre si es una parte de un dividido). */
  id: string;
  fecha: string;
  /** Gasto en positivo. */
  importe: IntegerAmount;
  payee: string | null;
  notas: string | null;
};

export function clavePuntual(clave: Clave, mes: string): string {
  return `${clave}|${mes}`;
}

/**
 * El movimiento de mayor importe (gasto) de cada categoría y mes de
 * `puntuales`. Las partes de un dividido cuentan por separado.
 */
export function elegirMovimientosPuntuales(
  filas: readonly FilaMovimiento[],
  puntuales: ReadonlyArray<Pick<PuntualDetectado, 'clave' | 'mes'>>,
): Map<string, MovimientoPuntual> {
  const buscados = new Set(puntuales.map(p => clavePuntual(p.clave, p.mes)));
  const elegidos = new Map<string, MovimientoPuntual>();
  for (const f of filas) {
    if (f.amount >= 0) {
      continue;
    }
    const k = clavePuntual(claveDe(f.category), f.date.slice(0, 7));
    if (!buscados.has(k)) {
      continue;
    }
    const actual = elegidos.get(k);
    if (!actual || -f.amount > actual.importe) {
      elegidos.set(k, {
        id: f.parent_id ?? f.id,
        fecha: f.date,
        importe: -f.amount,
        payee: f.payee,
        notas: f.notes,
      });
    }
  }
  return elegidos;
}

/** Largo máximo del concepto en el aviso. */
export const LARGO_CONCEPTO = 40;

/** Recorta un texto a `max` caracteres (con «…»), en una sola línea. */
export function recortarConcepto(texto: string, max = LARGO_CONCEPTO): string {
  const limpio = texto.replace(/\s+/g, ' ').trim();
  if (limpio.length <= max) {
    return limpio;
  }
  return `${limpio.slice(0, max - 1).trimEnd()}…`;
}

/** El beneficiario y, si no hay, las notas (recortadas); `null` si nada. */
export function conceptoMovimiento(
  nombrePayee: string | null | undefined,
  notas: string | null | undefined,
): string | null {
  for (const texto of [nombrePayee, notas]) {
    if (texto && texto.trim()) {
      return recortarConcepto(texto);
    }
  }
  return null;
}

type Traducir = (clave: string, opciones?: Record<string, unknown>) => string;

/**
 * Texto del aviso (sin el «Gasto puntual:» en negrita ni la coletilla):
 * con el movimiento, «649 € en 🏠 Hogar el 4 de octubre (Lavadora), 14
 * veces lo habitual.»; sin él, por meses.
 */
export function textoAvisoPuntual(
  t: Traducir,
  d: {
    importe: string;
    categoria: string;
    veces: number;
    /** Mes largo («Octubre 2026»), si no hay movimiento. */
    mes: string;
    movimiento?: { dia: number; nombreMes: string; concepto: string | null };
  },
): string {
  const times = Math.round(d.veces);
  if (!d.movimiento) {
    return t(
      '{{amount}} in {{category}} in {{month}}, {{times}} times the usual.',
      { amount: d.importe, category: d.categoria, month: d.mes, times },
    );
  }
  const opciones = {
    amount: d.importe,
    category: d.categoria,
    day: d.movimiento.dia,
    month: d.movimiento.nombreMes,
    concept: d.movimiento.concepto,
    times,
  };
  return d.movimiento.concepto
    ? t(
        '{{amount}} in {{category}} on {{month}} {{day}} ({{concept}}), {{times}} times the usual.',
        opciones,
      )
    : t(
        '{{amount}} in {{category}} on {{month}} {{day}}, {{times}} times the usual.',
        opciones,
      );
}

/**
 * Qué cuenta el aviso: el movimiento (su importe y cuántas veces lo
 * habitual es) si él solo explica el pico (importe ≥ lo que pasa de lo
 * habitual); si no, el gasto del mes, sin movimiento.
 */
export function avisoDePuntual(
  p: Pick<PuntualDetectado, 'importe' | 'habitual' | 'veces' | 'exceso'>,
  movimiento: MovimientoPuntual | undefined,
): { importe: IntegerAmount; veces: number; movimiento?: MovimientoPuntual } {
  if (movimiento && movimiento.importe >= p.exceso && p.habitual > 0) {
    return {
      importe: movimiento.importe,
      veces: movimiento.importe / p.habitual,
      movimiento,
    };
  }
  return { importe: p.importe, veces: p.veces };
}
